import { useState, useEffect } from 'react'
import { ArrowLeft, Loader2, Sparkles, ChevronDown, ChevronRight, Monitor, MessageSquare, PauseCircle, ArrowRightCircle, MonitorOff, Info, Hash, Clock, FileText, Copy, Check, HelpCircle, Globe } from 'lucide-react'
import { demoPrepApi, transcriptsApi, hubspotExcerptsApi } from '@/api/client'
import { showToast } from '@/store/uiStore'
import type { DemoPrepPlan, DemoSection, DemoStep, DemoRunsheet, StepType } from '../types'
import { STATUS_CONFIG, STEP_TYPE_CONFIG, PROSPECT_INDUSTRIES, isRunsheet } from '../types'

function unwrap(res: any) {
  return res?.data?.data || res?.data || null
}

function runsheetToText(runsheet: DemoRunsheet): string {
  const lines: string[] = []
  const ctx = runsheet.client_context

  // Client context
  if (ctx.terminology && Object.keys(ctx.terminology).length > 0) {
    lines.push('TERMINOLOGY')
    for (const [std, client] of Object.entries(ctx.terminology)) {
      lines.push(`  ${std} → ${client}`)
    }
    lines.push('')
  }
  if (ctx.demo_data?.length) {
    lines.push('DEMO DATA')
    ctx.demo_data.forEach(d => lines.push(`  ${d}`))
    lines.push('')
  }
  if (ctx.timing) lines.push(`TIMING: ${ctx.timing}`)
  if (ctx.notes) lines.push(`NOTES: ${ctx.notes}`)
  if (ctx.timing || ctx.notes) lines.push('')

  // Sections
  for (let si = 0; si < runsheet.sections.length; si++) {
    const section = runsheet.sections[si]
    lines.push(`${si + 1}. ${section.title}`)
    let stepNum = 0
    for (const step of section.steps) {
      const isBreak = step.type === 'pause' || step.type === 'handoff' || step.type === 'screen'
      if (isBreak) {
        const label = step.type === 'pause' ? '⏸' : step.type === 'handoff' ? '→' : '🖥'
        lines.push(`   ${label} ${step.action}`)
      } else {
        stepNum++
        const presenter = step.presenter ? ` [${step.presenter}]` : ''
        const notes = step.notes ? ` — ${step.notes}` : ''
        lines.push(`   ${stepNum}. ${step.action}${presenter}${notes}`)
      }
    }
    lines.push('')
  }

  // Discovery questions
  if (runsheet.discovery_questions?.length) {
    lines.push('DISCOVERY QUESTIONS')
    runsheet.discovery_questions.forEach((q, i) => lines.push(`  ${i + 1}. ${q}`))
    lines.push('')
  }

  return lines.join('\n').trim()
}

const STEP_ICONS: Record<StepType, typeof Monitor> = {
  demo: Monitor,
  talk: MessageSquare,
  pause: PauseCircle,
  handoff: ArrowRightCircle,
  screen: MonitorOff,
}

function StepRow({ step, index }: { step: DemoStep; index: number }) {
  const config = STEP_TYPE_CONFIG[step.type] || STEP_TYPE_CONFIG.demo
  const Icon = STEP_ICONS[step.type] || Monitor

  const isPause = step.type === 'pause'
  const isHandoff = step.type === 'handoff'
  const isScreen = step.type === 'screen'
  const isBreak = isPause || isHandoff || isScreen

  if (isBreak) {
    return (
      <div className={`flex items-center gap-3 px-3 py-2 rounded-lg ${config.bg} border border-dashed border-current/20`}>
        <Icon size={14} className={config.color} />
        <span className={`text-xs font-semibold uppercase tracking-wide ${config.color}`}>{step.action}</span>
        {step.presenter && (
          <span className="text-xs text-[var(--text-muted)] ml-auto">{step.presenter}</span>
        )}
      </div>
    )
  }

  return (
    <div className="flex items-start gap-3 px-3 py-1.5 group">
      <span className="text-xs font-mono text-[var(--text-muted)] mt-0.5 w-5 shrink-0 text-right">{index}</span>
      <Icon size={13} className={`${config.color} mt-0.5 shrink-0`} />
      <div className="flex-1 min-w-0">
        <span className="text-sm text-[var(--text-primary)]">{step.action}</span>
        {step.notes && (
          <p className="text-xs text-[var(--text-muted)] mt-0.5 italic">{step.notes}</p>
        )}
      </div>
      {step.presenter && (
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-muted)] shrink-0">{step.presenter}</span>
      )}
    </div>
  )
}

function RunsheetSection({ section, sectionIndex }: { section: DemoSection; sectionIndex: number }) {
  const [expanded, setExpanded] = useState(true)
  let stepNum = 0

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-[var(--bg-tertiary)] transition-colors"
      >
        <span className="w-6 h-6 rounded-full bg-orange-500/20 text-orange-400 text-xs flex items-center justify-center font-bold shrink-0">
          {sectionIndex + 1}
        </span>
        <span className="flex-1 text-sm font-semibold text-[var(--text-primary)]">{section.title}</span>
        <span className="text-xs text-[var(--text-muted)]">{section.steps.length} steps</span>
        {expanded ? <ChevronDown size={14} className="text-[var(--text-muted)]" /> : <ChevronRight size={14} className="text-[var(--text-muted)]" />}
      </button>
      {expanded && (
        <div className="px-1 pb-2 space-y-0.5">
          {section.steps.map((step, i) => {
            const isBreak = step.type === 'pause' || step.type === 'handoff' || step.type === 'screen'
            if (!isBreak) stepNum++
            return <StepRow key={i} step={step} index={isBreak ? 0 : stepNum} />
          })}
        </div>
      )}
    </div>
  )
}

function ClientContextCard({ context }: { context: DemoRunsheet['client_context'] }) {
  const [expanded, setExpanded] = useState(true)
  const termEntries = Object.entries(context.terminology || {})
  const hasTerms = termEntries.length > 0
  const hasData = (context.demo_data || []).length > 0

  if (!hasTerms && !hasData && !context.timing && !context.notes) return null

  return (
    <div className="rounded-lg border border-orange-500/20 bg-orange-500/5 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-orange-500/10 transition-colors"
      >
        <Info size={14} className="text-orange-400 shrink-0" />
        <span className="flex-1 text-sm font-semibold text-orange-400">Client Context</span>
        {context.timing && (
          <span className="flex items-center gap-1 text-xs text-orange-300"><Clock size={11} />{context.timing}</span>
        )}
        {expanded ? <ChevronDown size={14} className="text-orange-400" /> : <ChevronRight size={14} className="text-orange-400" />}
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-3">
          {context.notes && (
            <p className="text-xs text-[var(--text-secondary)]">{context.notes}</p>
          )}
          {hasTerms && (
            <div>
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1">Terminology</h4>
              <div className="flex flex-wrap gap-2">
                {termEntries.map(([standard, client]) => (
                  <span key={standard} className="text-xs px-2 py-1 rounded bg-[var(--bg-secondary)] text-[var(--text-secondary)]">
                    <span className="line-through text-[var(--text-muted)]">{standard}</span> → <span className="font-medium text-orange-400">{client}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {hasData && (
            <div>
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1">Demo Data</h4>
              <div className="space-y-1">
                {context.demo_data.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                    <Hash size={10} className="text-[var(--text-muted)] shrink-0" />
                    <span className="font-mono">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function DemoPlanDetail({ id, onBack }: { id: number; onBack: () => void }) {
  const [plan, setPlan] = useState<DemoPrepPlan | null>(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [clarifying, setClarifying] = useState(false)
  const [questions, setQuestions] = useState<string[]>([])
  const [answers, setAnswers] = useState<Record<number, string>>({})
  const [copied, setCopied] = useState(false)
  const [rawInput, setRawInput] = useState('')
  const [title, setTitle] = useState('')
  const [industry, setIndustry] = useState('')
  const [transcriptCount, setTranscriptCount] = useState(0)
  const [hasExcerpt, setHasExcerpt] = useState(false)
  const [dealContextLoading, setDealContextLoading] = useState(false)

  const loadDealContext = async (dealId: number) => {
    setDealContextLoading(true)
    const [tRes, eRes] = await Promise.all([
      transcriptsApi.list(dealId),
      hubspotExcerptsApi.get(dealId),
    ])
    if (tRes.success && tRes.data) setTranscriptCount(tRes.data.length)
    if (eRes.success && eRes.data) setHasExcerpt(true)
    setDealContextLoading(false)
  }

  const load = async () => {
    const res = await demoPrepApi.get(id)
    const data = unwrap(res)
    if (data) {
      const p = data as DemoPrepPlan
      setPlan(p)
      setRawInput(p.raw_input || '')
      setTitle(p.title)
      setIndustry(p.prospect_industry || '')
    }
    setLoading(false)
  }

  useEffect(() => {
    load().then(() => {
      // After plan loads, check for deal context
    })
  }, [id])

  useEffect(() => {
    if (plan?.deal_id) loadDealContext(plan.deal_id)
  }, [plan?.deal_id])

  const saveFields = async () => {
    await demoPrepApi.update(id, {
      raw_input: rawInput,
      title: title || 'Untitled Demo Plan',
      prospect_industry: industry || null,
    })
  }

  const handleClarify = async () => {
    if (!plan) return
    setClarifying(true)
    await saveFields()

    const res = await demoPrepApi.clarify(id)
    const data = unwrap(res)
    if (data?.questions?.length) {
      setQuestions(data.questions)
      setAnswers({})
    } else {
      showToast('No questions needed — ready to generate', 'success')
    }
    setClarifying(false)
  }

  const handleGenerate = async () => {
    if (!plan) return
    setGenerating(true)
    await saveFields()

    // Build clarify answers string from Q&A pairs
    let clarifyAnswers: string | undefined
    if (questions.length > 0) {
      const qaPairs = questions
        .map((q, i) => answers[i]?.trim() ? `Q: ${q}\nA: ${answers[i].trim()}` : null)
        .filter(Boolean)
      if (qaPairs.length > 0) clarifyAnswers = qaPairs.join('\n\n')
    }

    const res = await demoPrepApi.generate(id, clarifyAnswers)
    const data = unwrap(res)
    if (data) {
      setPlan(data as DemoPrepPlan)
      setQuestions([])
      setAnswers({})
      showToast('Demo runsheet generated', 'success')
    } else {
      showToast(res?.error || 'Generation failed', 'error')
      await load()
    }
    setGenerating(false)
  }

  const handleSave = async () => {
    if (!plan) return
    await saveFields()
    showToast('Saved', 'success')
  }

  const handleIndustryChange = async (value: string) => {
    setIndustry(value)
    if (plan) {
      await demoPrepApi.update(id, { prospect_industry: value || null })
    }
  }

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin text-[var(--text-muted)]" /></div>
  }

  if (!plan) {
    return <div className="text-center py-16 text-[var(--text-muted)]">Demo plan not found</div>
  }

  const status = STATUS_CONFIG[plan.status] || STATUS_CONFIG.draft
  const runsheet = isRunsheet(plan.talking_points) ? plan.talking_points : null
  const hasContent = runsheet !== null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <button onClick={onBack} className="mt-1 p-1 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)]">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              onBlur={handleSave}
              className="text-lg font-semibold text-[var(--text-primary)] bg-transparent border-none outline-none w-full"
              placeholder="Demo plan title..."
            />
            <div className="flex items-center gap-3 mt-1">
              <span className={`text-xs font-medium ${status.color}`}>{status.label}</span>
              {plan.customer_name && (
                <span className="text-xs text-[var(--text-muted)]">{plan.customer_name}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Industry Selector */}
      <div className="flex items-center gap-3">
        <label className="text-xs text-[var(--text-muted)] shrink-0">Industry:</label>
        <select
          value={industry}
          onChange={e => handleIndustryChange(e.target.value)}
          className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-3 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-color)]"
        >
          <option value="">Select industry...</option>
          {PROSPECT_INDUSTRIES.map(ind => (
            <option key={ind.value} value={ind.value}>{ind.label}</option>
          ))}
        </select>
      </div>

      {/* Deal Context Sources */}
      {plan.deal_id && (transcriptCount > 0 || hasExcerpt) && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-cyan-500/20 bg-cyan-500/5">
          <Info size={14} className="text-cyan-400 shrink-0" />
          <span className="text-xs text-[var(--text-secondary)]">
            Linked deal context will be included automatically:
          </span>
          <div className="flex items-center gap-2">
            {transcriptCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-cyan-500/15 text-cyan-400 rounded">
                <FileText size={10} /> {transcriptCount} transcript{transcriptCount !== 1 ? 's' : ''}
              </span>
            )}
            {hasExcerpt && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-cyan-500/15 text-cyan-400 rounded">
                <Globe size={10} /> HubSpot excerpt
              </span>
            )}
          </div>
        </div>
      )}

      {plan.deal_id && !dealContextLoading && transcriptCount === 0 && !hasExcerpt && (
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <Info size={14} className="text-[var(--text-muted)] shrink-0" />
          <span className="text-xs text-[var(--text-muted)]">
            No transcripts or HubSpot excerpt found for this deal. Add them via the Transcripts tool to enrich the AI context.
          </span>
        </div>
      )}

      {plan.error_message && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-400">
          {plan.error_message}
        </div>
      )}

      {/* Step 1: Raw Input */}
      <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
            <FileText size={14} className="text-orange-400" />
            Paste Raw Context
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClarify}
              disabled={clarifying || generating || (!rawInput.trim() && transcriptCount === 0 && !hasExcerpt)}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors disabled:opacity-50"
            >
              {clarifying ? (
                <><Loader2 size={14} className="animate-spin" /> Thinking...</>
              ) : (
                <><HelpCircle size={14} /> Prepare Questions</>
              )}
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating || clarifying || (!rawInput.trim() && transcriptCount === 0 && !hasExcerpt)}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-orange-600 text-white hover:bg-orange-500 transition-colors disabled:opacity-50"
            >
              {generating ? (
                <><Loader2 size={14} className="animate-spin" /> Generating...</>
              ) : (
                <><Sparkles size={14} /> {hasContent ? 'Regenerate' : 'Generate'}</>
              )}
            </button>
          </div>
        </div>
        <p className="text-xs text-[var(--text-muted)] mb-3">
          Paste emails, meeting notes, raw demo notes, or any context. {plan.deal_id ? 'Saved transcripts and HubSpot excerpts for the linked deal are included automatically. ' : ''}Hit "Prepare Questions" to clarify gaps first, or "Generate" to go straight to the runsheet.
        </p>
        <textarea
          value={rawInput}
          onChange={e => setRawInput(e.target.value)}
          onBlur={handleSave}
          rows={10}
          disabled={generating || clarifying}
          className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-3 text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] resize-y focus:outline-none focus:border-[var(--accent-color)] disabled:opacity-50 font-mono text-xs leading-relaxed"
          placeholder={`e.g.\nPortal based demo first.\nCreate org, show UBO report.\nShow KYB then send KYC link.\nSTOP SHARING - Sales to check in.\nShow risk scoring and analytics.\n\nClient uses "family" instead of "organisation".\nDemo company: ACME PTY LTD (ACN: 123456789)\nTotal time: 45 minutes`}
        />
      </div>

      {/* Step 1.5: Clarifying Questions */}
      {questions.length > 0 && (
        <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-4 space-y-4">
          <h3 className="text-sm font-semibold text-blue-400 flex items-center gap-2">
            <HelpCircle size={14} />
            A few questions before generating
          </h3>
          <div className="space-y-3">
            {questions.map((q, i) => (
              <div key={i}>
                <label className="text-sm text-[var(--text-primary)] block mb-1">{q}</label>
                <input
                  value={answers[i] || ''}
                  onChange={e => setAnswers(prev => ({ ...prev, [i]: e.target.value }))}
                  className="w-full rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-blue-500"
                  placeholder="Type your answer..."
                />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-orange-600 text-white hover:bg-orange-500 transition-colors disabled:opacity-50"
            >
              {generating ? (
                <><Loader2 size={14} className="animate-spin" /> Generating...</>
              ) : (
                <><Sparkles size={14} /> Generate Runsheet</>
              )}
            </button>
            <button
              onClick={() => { setQuestions([]); setAnswers({}) }}
              className="px-3 py-2 text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Demo Runsheet */}
      {runsheet && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
              <Sparkles size={14} className="text-orange-400" />
              Demo Runsheet
              <span className="text-xs font-normal text-[var(--text-muted)]">
                ({runsheet.sections.length} sections, {runsheet.sections.reduce((t, s) => t + s.steps.length, 0)} steps)
              </span>
            </h3>
            <button
              onClick={() => {
                navigator.clipboard.writeText(runsheetToText(runsheet))
                setCopied(true)
                showToast('Runsheet copied to clipboard', 'success')
                setTimeout(() => setCopied(false), 2000)
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-[var(--border-color)] hover:bg-[var(--bg-tertiary)] text-[var(--text-secondary)] transition-colors"
            >
              {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          <ClientContextCard context={runsheet.client_context} />

          <div className="space-y-3">
            {runsheet.sections.map((section, i) => (
              <RunsheetSection key={i} section={section} sectionIndex={i} />
            ))}
          </div>

          {/* Discovery Questions */}
          {runsheet.discovery_questions && runsheet.discovery_questions.length > 0 && (
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 overflow-hidden">
              <div className="flex items-center gap-3 p-3">
                <HelpCircle size={14} className="text-blue-400 shrink-0" />
                <span className="text-sm font-semibold text-blue-400">Discovery Questions</span>
                <span className="text-xs text-blue-400/60">{runsheet.discovery_questions.length} questions to ask during the demo</span>
              </div>
              <div className="px-3 pb-3 space-y-2">
                {runsheet.discovery_questions.map((q, i) => (
                  <div key={i} className="flex items-start gap-3 px-3 py-1.5">
                    <span className="text-xs font-mono text-blue-400/60 mt-0.5 w-5 shrink-0 text-right">{i + 1}</span>
                    <span className="text-sm text-[var(--text-primary)]">{q}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
